import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ROUTE_360_VIEW_FULLSCREEN } from '../../routes'
import { navigateTo } from '../../store/history/history.actions'
import { selectIsMapFullScreen } from '../../store/map/map.selectors'
import { displayMapPopup, hideMapPopup } from '../../store/map/mapSlice'
import { selectLocale } from '../../store/navigation/navigation.selectors'
import { displayOutdoor } from '../../store/ui/ui.actions'
import { selectIsOutdoorPrompted } from '../../store/ui/ui.outdoor.selectors'
import person from '../genericComponents/assets/person.svg'
import { useMapContext } from '../map/MapContext'
import { withMapInitialized } from '../map/map.utils'
import { POPUP_TYPE_PANO } from '../map/popup/popup.selectors'
import SVGIcon from '../svg/SVGIcon'
import { useAction } from '../utils/react-redux'
import useTimeout from '../utils/useTimeout'
import s from './UPI.less'
import { OUTDOOR_STATUS_PROMPT, fetchPano } from './Utils'

const UPI_SIZE = 30
const PREVIEW_DELAY = 500
const MAP_OFFSET_X = 415
const MAP_OFFSET_Y = 70

const useOutdoorPreviewPopup = () => {
  const dispatch = useDispatch()
  const execDisplayOutdoor = useAction(displayOutdoor)
  const locale = useSelector(selectLocale)

  const displayPreview = useCallback(
    coords => {
      fetchPano(coords, locale)
        .catch(() => console.debug('Pas de pano', coords))
        .then(panoData => {
          execDisplayOutdoor({ ...panoData, ...coords, status: OUTDOOR_STATUS_PROMPT })
          dispatch(displayMapPopup(POPUP_TYPE_PANO))
        })
    },
    [locale]
  )

  const closePreview = useCallback(() => {
    dispatch(hideMapPopup())
    execDisplayOutdoor({ status: OUTDOOR_STATUS_PROMPT })
  }, [])

  return {
    displayPreview,
    closePreview
  }
}

const UPI = () => {
  const { map } = useMapContext()
  const isDisplayed = useSelector(selectIsOutdoorPrompted)
  const isMapFullScreen = useSelector(selectIsMapFullScreen)
  const execNavigateTo = useAction(navigateTo)
  const offsetX = isMapFullScreen ? 0 : MAP_OFFSET_X
  const locale = useSelector(selectLocale)

  const elementRef = useRef()
  const [coords, setCoords] = useState()
  const [setPreviewTimeout, clearPreviewTimeout] = useTimeout(PREVIEW_DELAY)
  const { displayPreview, closePreview } = useOutdoorPreviewPopup()

  useEffect(() => {
    if (isDisplayed) {
      follow()
    } else {
      unfollow()
    }
  }, [isDisplayed])

  useEffect(() => {
    if (coords) {
      closePreview()
      clearPreviewTimeout()
      setPreviewTimeout(() => displayPreview(coords))
    }
  }, [coords])

  const follow = () => {
    map.on('click', mapClicked)
    map.on('mousemove', mapMouseMove)
    map.getCanvas().style.cursor = 'pointer'
    document.querySelector('body').addEventListener('mousemove', bodyMouseMove)
  }

  const unfollow = () => {
    map.off('click', mapClicked)
    map.off('mousemove', mapMouseMove)
    map.getCanvas().style.cursor = ''
    document.querySelector('body').removeEventListener('mousemove', bodyMouseMove)
    clearPreviewTimeout()
  }

  const launchOutdoor = coords => {
    closePreview()
    unfollow()
    fetchPano(coords, locale)
      .then(panoData => {
        if (panoData?.panoramicId) {
          execNavigateTo({
            route: ROUTE_360_VIEW_FULLSCREEN,
            routeOptions: { panoramicId: panoData.panoramicId }
          })
        }
      })
      .catch(() => {
        follow()
        displayPreview(coords)
      })
  }

  const onClicked = () => {
    launchOutdoor(coords)
  }

  const mapClicked = event => {
    launchOutdoor(event.lngLat)
  }

  const mapMouseMove = useCallback(
    event => {
      setCoords(event.lngLat)
    },
    [setCoords]
  )

  const onMouseMove = useCallback(
    ({ x, y }) => {
      if (!elementRef.current) return
      elementRef.current.style.top = `${y - UPI_SIZE - 7}px`
      elementRef.current.style.left = `${x - UPI_SIZE / 2}px`
    },
    [elementRef.current]
  )

  const bodyMouseMove = useCallback(
    event => {
      const { clientX, clientY } = event
      onMouseMove({ x: clientX - offsetX, y: clientY - MAP_OFFSET_Y })
    },
    [onMouseMove, offsetX]
  )

  if (!isDisplayed) return <></>

  return (
    <div ref={elementRef} className={s.root} onClick={onClicked}>
      <SVGIcon url={person} />
    </div>
  )
}

export default withMapInitialized(UPI)
